import { WireBridgeResizeService } from '../../../processor/wire-bridge-resize.service';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUtils } from '@article/util/article-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { Injectable } from '@angular/core';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ArrayUtils } from '@shared/util/array-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ProjectComponentPresenter } from '../../project-component.presenter';

export type WireBridgeDisplayMode = 'flagMode' | 'wireMode';

@Injectable({ providedIn: 'root' })
export class WireBridgeService {
  public static readonly FLAG_OBJECT_NAME: string = 'flagBridgeComponent';
  public static readonly WIRE_OBJECT_NAME: string = 'wireBridgeComponent';
  public static readonly WIRECONTOUR_NAME: string = 'wirecontour';

  public static readonly DEFAULT_LEFT_EDGE_RANGE: number = 1;
  public static readonly DEFAULT_RIGHT_EDGE_RANGE: number = 2;

  private currentMode: WireBridgeDisplayMode = 'flagMode';

  constructor(
    private projectArticleService: ProjectArticleService,
    private containerService: ContainerService,
    private wireBridgeResizeService: WireBridgeResizeService,
    private sceneManager: SceneManager,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) {
  }

  public createJumpersWhileLoadingProject(): void {
    const articleContainers: ArticleContainer[] = this.enumerateArticleContainersRequireJumper();
    this.internalCreateJumpers(articleContainers, true);
  }

  public undoAttachOperationCompleted_handler(attachedArticleUsageIds: string[]): void {
    let articleContainers: ArticleContainer[] = this.containerService.findArticleContainersInGlobalByGuids(attachedArticleUsageIds);
    articleContainers = this.filterArticleContainersRequireJumper(articleContainers);
    this.internalCreateJumpers(articleContainers, true);
  }

  public createJumpersBetweenArticles(articleContainers: ArticleContainer[]): void {
    const filteredContainers: ArticleContainer[] = this.filterArticleContainersRequireJumper(articleContainers);
    this.cleanWireLabelProperties(filteredContainers);
    this.internalCreateJumpers(filteredContainers);
  }

  public updateWireBridgeObjects(articleContainersToUpdate: ArticleContainer[]) {
    const wireBridges = articleContainersToUpdate.filter(c => ArticleUtils.isWireBridgeArticle(c.article));
    this.wireBridgeResizeService.updateWireBridges(wireBridges, this.isWireMode());
  }


  public async setWireMode(): Promise<void> {
    if (this.currentMode !== 'wireMode') {
      this.activateMode('wireMode');
      await this.wireBridgeResizeService.updateWireBridges(this.enumerateArticleContainersRequireJumper(), true)
      this.sceneManager.renderScene();
    }
  }

  public setFlagMode(): void {
    if (this.currentMode !== 'flagMode') {
      this.activateMode('flagMode');
      this.sceneManager.renderScene();
    }
  }

  public isWireMode(): boolean {
    return this.currentMode === 'wireMode';
  }

  public activateMode(mode: WireBridgeDisplayMode): void {
    this.currentMode = mode;
    this.applyCurrentModeForArticles(this.enumerateArticleContainersRequireJumper());
  }

  public applyCurrentModeForArticles(articleContainers: ArticleContainer[]): void {
    switch (this.currentMode) {
      case 'flagMode':
        this.applyArticlesFlagMode(articleContainers);
        break;
      case 'wireMode':
        this.applyArticlesWireMode(articleContainers);
        break;
    }
  }

  private cleanWireLabelProperties(wireBridgeContainers: ArticleContainer[]): void {
    for (const container of wireBridgeContainers) {
      if (!container.articleUsage) {
        continue;
      }
      PropertyUtils.removePropertiesByNames(container.articleUsage.infoProperties, [ArticleUsageInfoPropertyDef.WIRE_LABEL]);
    }
  }

  private internalCreateJumpers(filteredArticleContainers: ArticleContainer[], skipWireLengthCalculation: boolean = false): void {
    for (const wireBridgeContainer of filteredArticleContainers) {
      wireBridgeContainer.name = ArticleContainer.WIRE_LINE_NAME
    }
    this.wireBridgeResizeService.updateWireResolution(this.enumerateArticleContainersRequireJumper().length);
    this.wireBridgeResizeService.createWireBridges(filteredArticleContainers, skipWireLengthCalculation, this.currentMode == 'wireMode');

    const processedArticleGUIDs: string[] = [];
    for (const articleContainer of filteredArticleContainers) {
      const articleUsage: ArticleUsageVO = articleContainer.articleUsage;
      // article can be placed only in one group
      if (articleUsage && processedArticleGUIDs.indexOf(articleUsage.id) < 0) {
        const groupedUsageIds: string[] = this.projectArticleService.collectGroupArticleUsageIds(articleUsage);
        ArrayUtils.addAll(processedArticleGUIDs, groupedUsageIds);
      }
    }

    this.activateMode(this.currentMode);
  }

  private applyArticlesFlagMode(articleContainers: ArticleContainer[]): void {
    for (const articleContainer of articleContainers) {
      this.hideArticleComponentsByName(articleContainer, WireBridgeService.WIRE_OBJECT_NAME);
      this.showArticleComponentsByName(articleContainer, WireBridgeService.FLAG_OBJECT_NAME);
    }
  }

  private applyArticlesWireMode(articleContainers: ArticleContainer[]): void {
    for (const articleContainer of articleContainers) {
      this.hideArticleComponentsByName(articleContainer, WireBridgeService.FLAG_OBJECT_NAME);
      this.showArticleComponentsByName(articleContainer, WireBridgeService.WIRE_OBJECT_NAME);
    }
  }


  private isArticleRequiresJumper(articleContainer: ArticleContainer): boolean {
    let isRequiresJumper: boolean =!ArticleUsageUtils.isArticleUsageWithout3DModel(articleContainer.articleUsage);
    if (isRequiresJumper) {
      isRequiresJumper = ArticleUtils.isWireBridgeArticle(articleContainer.article);
    }
    return isRequiresJumper;
  }

  private enumerateArticleContainersRequireJumper(): ArticleContainer[] {
    const containersOnScene: ArticleContainer[] = this.containerService.enumerateArticleContainersInGlobal();
    return this.filterArticleContainersRequireJumper(containersOnScene);
  }

  private filterArticleContainersRequireJumper(articleContainers: ArticleContainer[]): ArticleContainer[] {
    const containersRequireJumper: ArticleContainer[] = new Array<ArticleContainer>();
    for (const container of articleContainers) {
      if (this.isArticleRequiresJumper(container)) {
        containersRequireJumper.push(container);
      }
    }
    return containersRequireJumper;
  }

  private showArticleComponentsByName(article: ArticleContainer, name: string): boolean {
    return Object3DUtils.changeChildrenVisibilityByName(article, name, true);
  }

  private hideArticleComponentsByName(article: ArticleContainer, name: string): boolean {
    return Object3DUtils.changeChildrenVisibilityByName(article, name, false);
  }

  public openWireBridgePopupAfterAddingOpenEndedWire(lastAddedArticle: ArticleVO): void {
    if(lastAddedArticle.type.displayName == ArticleTypeConstants.TYPE_WIRE_BRIDGE) {
      const lastAddedArticleUsage = this.projectArticleService.findLastAddedArticleUsages(2);
      /** If the last two added articleUsages have different usageIds, it must be an open-ended wire */
      if(lastAddedArticleUsage.length < 2 || lastAddedArticleUsage[0].groupId != lastAddedArticleUsage[1].groupId) {
        this.projectComponentPresenter.openWireBridgeInfoPopup(lastAddedArticleUsage[0])
      }
    }
  }
}
